import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  confirmPasswordReset
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  listAll
} from 'firebase/storage';
import { 
  getMessaging, 
  getToken, 
  onMessage, 
  isSupported 
} from 'firebase/messaging';

// Firebase configuration - with debugging for production
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyDmRrmUZLpSes7Y4Geq88b-HTKT8jP65Hs',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'nistylez-b2a1c.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'nistylez-b2a1c',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'nistylez-b2a1c.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '259583770450',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:259583770450:web:ec529de100b92eb06bc48b',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase only if we have a valid API key
let app, auth, db, storage, messaging;

try {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
  const isConfigured = apiKey && 
                      apiKey !== 'demo-api-key' && 
                      apiKey !== 'undefined' && 
                      apiKey !== 'null' && 
                      apiKey.startsWith('AIza'); // Firebase API keys start with 'AIza'
  
  if (isConfigured) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    messaging = getMessaging(app);
  } else {
    // Only show warning in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Firebase API key not configured. Firebase services will be disabled.');
    }
  }
} catch (error) {
  // Only show errors in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Firebase initialization error:', error);
    console.warn('Firebase services will be disabled.');
  }
}

class FirebaseService {
  constructor() {
    this.auth = auth || null;
    this.db = db || null;
    this.storage = storage || null;
    this.messaging = messaging || null;
    this.currentUser = null;
    this.chatListeners = new Map();
    this.notificationListeners = new Map();
    this.isFirebaseEnabled = !!(auth && db && storage);
  }

  // ==================== AUTHENTICATION ====================

  // Sign in with email and password
  async signIn(email, password) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      this.currentUser = userCredential.user;
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Firebase sign in error:', error);
      return { success: false, error: error.message };
    }
  }

  // Sign up with email and password
  async signUp(email, password, displayName) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      
      // Update profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      
      this.currentUser = userCredential.user;
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Firebase sign up error:', error);
      return { success: false, error: error.message };
    }
  }

  // Sign out
  async signOut() {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await signOut(this.auth);
      this.currentUser = null;
      return { success: true };
    } catch (error) {
      console.error('Firebase sign out error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current user
  getCurrentUser() {
    if (!this.isFirebaseEnabled) {
      return null;
    }
    return this.currentUser || this.auth.currentUser;
  }

  // Listen to auth state changes
  onAuthStateChanged(callback) {
    if (!this.isFirebaseEnabled) {
      console.warn('Firebase is not configured. Auth state changes will not be tracked.');
      return () => {};
    }
    
    return onAuthStateChanged(this.auth, (user) => {
      this.currentUser = user;
      callback(user);
    });
  }

  // Password reset
  async sendPasswordReset(email) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await sendPasswordResetEmail(this.auth, email);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false, error: error.message };
    }
  }

  // Confirm password reset
  async confirmPasswordReset(code, newPassword) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await confirmPasswordReset(this.auth, code, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Confirm password reset error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== CHAT SYSTEM ====================

  // Create or get chat session
  async createChatSession(participants, initialMessage = null) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const sessionData = {
        participants: participants.sort(), // Sort for consistent ID
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: initialMessage,
        status: 'active'
      };

      const docRef = await addDoc(collection(this.db, 'chat_sessions'), sessionData);
      
      if (initialMessage) {
        await this.sendMessage(docRef.id, initialMessage);
      }

      return { success: true, sessionId: docRef.id };
    } catch (error) {
      console.error('Create chat session error:', error);
      return { success: false, error: error.message };
    }
  }

  // Send message
  async sendMessage(sessionId, messageData) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const message = {
        sessionId,
        senderId: this.getCurrentUser()?.uid,
        senderName: this.getCurrentUser()?.displayName || 'User',
        content: messageData.content,
        type: messageData.type || 'text', // text, image, file
        timestamp: serverTimestamp(),
        read: false,
        ...messageData
      };

      const docRef = await addDoc(collection(this.db, 'messages'), message);
      
      // Update session with last message
      await updateDoc(doc(this.db, 'chat_sessions', sessionId), {
        lastMessage: message,
        updatedAt: serverTimestamp()
      });

      return { success: true, messageId: docRef.id };
    } catch (error) {
      console.error('Send message error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get chat messages
  async getChatMessages(sessionId, limit = 50) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const q = query(
        collection(this.db, 'messages'),
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );

      const querySnapshot = await getDocs(q);
      const messages = [];
      
      querySnapshot.forEach((doc) => {
        messages.push({ id: doc.id, ...doc.data() });
      });

      return { success: true, messages: messages.reverse() };
    } catch (error) {
      console.error('Get chat messages error:', error);
      return { success: false, error: error.message };
    }
  }

  // Listen to chat messages in real-time
  listenToChat(sessionId, callback) {
    if (!this.isFirebaseEnabled) {
      console.warn('Firebase is not configured. Chat listening will be disabled.');
      return null;
    }
    
    try {
      const q = query(
        collection(this.db, 'messages'),
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'asc')
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const messages = [];
        querySnapshot.forEach((doc) => {
          messages.push({ id: doc.id, ...doc.data() });
        });
        callback(messages);
      });

      this.chatListeners.set(sessionId, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error('Listen to chat error:', error);
      return null;
    }
  }

  // Stop listening to chat
  stopListeningToChat(sessionId) {
    const unsubscribe = this.chatListeners.get(sessionId);
    if (unsubscribe) {
      unsubscribe();
      this.chatListeners.delete(sessionId);
    }
  }

  // Mark message as read
  async markMessageAsRead(messageId) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await updateDoc(doc(this.db, 'messages', messageId), {
        read: true,
        readAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Mark message as read error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== NOTIFICATIONS ====================

  // Send notification
  async sendNotification(recipientId, notificationData) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const notification = {
        recipientId,
        senderId: this.getCurrentUser()?.uid,
        title: notificationData.title,
        body: notificationData.body,
        type: notificationData.type || 'general', // general, booking, payment, chat
        data: notificationData.data || {},
        read: false,
        timestamp: serverTimestamp()
      };

      const docRef = await addDoc(collection(this.db, 'notifications'), notification);
      return { success: true, notificationId: docRef.id };
    } catch (error) {
      console.error('Send notification error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user notifications
  async getUserNotifications(userId, limit = 20) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const q = query(
        collection(this.db, 'notifications'),
        where('recipientId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );

      const querySnapshot = await getDocs(q);
      const notifications = [];
      
      querySnapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
      });

      return { success: true, notifications };
    } catch (error) {
      console.error('Get notifications error:', error);
      return { success: false, error: error.message };
    }
  }

  // Listen to notifications in real-time
  listenToNotifications(userId, callback) {
    if (!this.isFirebaseEnabled) {
      console.warn('Firebase is not configured. Notification listening will be disabled.');
      return null;
    }
    
    try {
      const q = query(
        collection(this.db, 'notifications'),
        where('recipientId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(50)
      );

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const notifications = [];
        querySnapshot.forEach((doc) => {
          notifications.push({ id: doc.id, ...doc.data() });
        });
        callback(notifications);
      });

      this.notificationListeners.set(userId, unsubscribe);
      return unsubscribe;
    } catch (error) {
      console.error('Listen to notifications error:', error);
      return null;
    }
  }

  // Mark notification as read
  async markNotificationAsRead(notificationId) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await updateDoc(doc(this.db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Mark notification as read error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== FILE UPLOAD ====================

  // Upload file to Firebase Storage
  async uploadFile(file, path, metadata = {}) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const storageRef = ref(this.storage, path);
      const uploadResult = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      return { 
        success: true, 
        url: downloadURL, 
        path: path,
        metadata: uploadResult.metadata
      };
    } catch (error) {
      console.error('File upload error:', error);
      return { success: false, error: error.message };
    }
  }

  // Upload profile image
  async uploadProfileImage(file, userId) {
    const path = `profile-images/${userId}/${Date.now()}_${file.name}`;
    return this.uploadFile(file, path, {
      contentType: file.type,
      customMetadata: {
        uploadedBy: userId,
        type: 'profile-image'
      }
    });
  }

  // Upload chat image
  async uploadChatImage(file, sessionId) {
    const path = `chat-images/${sessionId}/${Date.now()}_${file.name}`;
    return this.uploadFile(file, path, {
      contentType: file.type,
      customMetadata: {
        sessionId,
        type: 'chat-image'
      }
    });
  }

  // Delete file
  async deleteFile(path) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      const fileRef = ref(this.storage, path);
      await deleteObject(fileRef);
      return { success: true };
    } catch (error) {
      console.error('Delete file error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== PUSH NOTIFICATIONS ====================

  // Request notification permission
  async requestNotificationPermission() {
    try {
      if (!isSupported()) {
        return { success: false, error: 'Push notifications not supported' };
      }

      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        return { success: true };
      } else {
        return { success: false, error: 'Permission denied' };
      }
    } catch (error) {
      console.error('Request notification permission error:', error);
      return { success: false, error: error.message };
    }
  }

  // Get FCM token
  async getFCMToken() {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      if (!isSupported()) {
        return { success: false, error: 'Push notifications not supported' };
      }

      const token = await getToken(this.messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY || 'BEVRPx-OOqUPusEVKeqFbfvtjybQ05yi2ZGhnrHA5ARMhjvCPIv8hMYlWOXavGZZYi8TRzSkMvL3d7ZKLWcsgpw'
      });

      if (token) {
        return { success: true, token };
      } else {
        return { success: false, error: 'No registration token available' };
      }
    } catch (error) {
      console.error('Get FCM token error:', error);
      return { success: false, error: error.message };
    }
  }

  // Listen to foreground messages
  onForegroundMessage(callback) {
    if (!this.isFirebaseEnabled) {
      console.warn('Firebase is not configured. Foreground message listening will be disabled.');
      return null;
    }
    
    if (!isSupported()) {
      return null;
    }

    return onMessage(this.messaging, (payload) => {
      callback(payload);
    });
  }

  // Save FCM token to user profile
  async saveFCMToken(userId, token) {
    if (!this.isFirebaseEnabled) {
      return { success: false, error: 'Firebase is not configured. Please check your environment variables.' };
    }
    
    try {
      await updateDoc(doc(this.db, 'users', userId), {
        fcmToken: token,
        lastTokenUpdate: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Save FCM token error:', error);
      return { success: false, error: error.message };
    }
  }

  // ==================== UTILITY METHODS ====================

  // Convert Firestore timestamp to Date
  convertTimestamp(timestamp) {
    if (timestamp instanceof Timestamp) {
      return timestamp.toDate();
    }
    return timestamp;
  }

  // Clean up listeners
  cleanup() {
    // Stop all chat listeners
    this.chatListeners.forEach((unsubscribe) => unsubscribe());
    this.chatListeners.clear();

    // Stop all notification listeners
    this.notificationListeners.forEach((unsubscribe) => unsubscribe());
    this.notificationListeners.clear();
  }
}

// Create singleton instance
const firebaseService = new FirebaseService();

export default firebaseService;
